import {
  GetIwocapayPaymentCapturedResponse,
  GetFundingRequirementsIwocapayResponse,
} from '@iwoca/lapi-client/edge';

import {
  Account,
  Accounts,
  Application,
  ApplicationState,
  CancelledPlan,
  CapturedPayment,
  CapturedPaymentsResponse,
  EndedPaymentPlan,
  FormalOffer,
  OngoingPaymentPlan,
  PayLinksResponse,
  PaymentPlan,
  Plan,
  ProductOffers,
  SuggestedProduct,
  SuggestedProductType,
} from './Dashboard.types';
import { useGetAccounts } from '../../api/lending/lapiHooks';

export const getIwocaPayProductOffers = (
  product_offers: ProductOffers,
): ProductOffers['products'][number]['offers'] | null => {
  for (const product of product_offers.products) {
    if (product.product_type !== 'iwocapay') continue;
    if (!product.offers.length) continue;

    return product.offers;
  }

  return null;
};

const getIwocaPayFormalOffersFromAccounts = (accounts: Accounts) => {
  for (const account of accounts) {
    if (!account.formal_offers) continue;

    return account.formal_offers.filter(
      (offer) => offer.product_type === 'iwocapay',
    );
  }

  return [];
};

const getOfferWithHighestAvailableCash = (
  formalOffers: FormalOffer[],
): FormalOffer | null => {
  let highestCashOffer = null;

  for (const formalOffer of formalOffers) {
    if (
      !highestCashOffer ||
      formalOffer.available_cash! > highestCashOffer.available_cash!
    ) {
      highestCashOffer = formalOffer;
    }
  }

  return highestCashOffer;
};

const getIwocaPayFormalOfferWithHighestLimit = (accounts: Accounts) => {
  const iwocaPayOffers = getIwocaPayFormalOffersFromAccounts(accounts);

  return getOfferWithHighestAvailableCash(iwocaPayOffers);
};

export const getIwocaPayApplication = (
  application: Application,
): Application | null => {
  for (const requestedProduct of application.requested_products || []) {
    if (requestedProduct.product_type === 'iwocapay') {
      return application;
    }
  }

  return null;
};

const getLatestUnfundedPayLink = (
  payLinks: PayLinksResponse,
): PayLinksResponse[number] | null => {
  if (!payLinks.length) return null;

  const latestPayLink = payLinks[0];
  if (latestPayLink.is_funded) return null;

  return latestPayLink;
};

const getIsCreditLimitInsufficient = ({
  formalOffer,
  latestUnfundedPayLink,
  totalScheduledAmount,
}: {
  formalOffer: FormalOffer;
  latestUnfundedPayLink: PayLinksResponse[number];
  totalScheduledAmount: number;
}) => {
  if (formalOffer.available_cash === undefined) return true;
  if (formalOffer.available_cash === 0) return true;

  return (
    formalOffer.available_cash <
    latestUnfundedPayLink.amount + totalScheduledAmount
  );
};

const getCapturePayment = ({
  latestUnfundedPayLink,
  capturedPayments,
}: {
  latestUnfundedPayLink: PayLinksResponse[number];
  capturedPayments?: CapturedPaymentsResponse;
}) => {
  if (!capturedPayments) return;

  return capturedPayments.find(
    (payment) => payment.pay_link.id === latestUnfundedPayLink.id,
  );
};

export const getApplicationState = ({
  payLinks,
  accounts,
  application,
  totalScheduledAmount,
  capturedPayments,
}: {
  payLinks: PayLinksResponse;
  accounts: Accounts;
  application: Application;
  totalScheduledAmount: number;
  capturedPayments?: CapturedPaymentsResponse;
}): ApplicationState => {
  const latestUnfundedPayLink = getLatestUnfundedPayLink(payLinks);
  const iwocaPayApplication = getIwocaPayApplication(application);

  const formalOfferWithHighestLimit =
    getIwocaPayFormalOfferWithHighestLimit(accounts);

  if (!latestUnfundedPayLink) return null;

  const capturedPaylink = getCapturePayment({
    latestUnfundedPayLink,
    capturedPayments,
  });
  if (capturedPaylink) return null;

  if (formalOfferWithHighestLimit?.is_valid) {
    const isCreditLimitInsufficient = getIsCreditLimitInsufficient({
      formalOffer: formalOfferWithHighestLimit,
      latestUnfundedPayLink,
      totalScheduledAmount,
    });

    if (isCreditLimitInsufficient) {
      return {
        pay_link_id: latestUnfundedPayLink.id,
        status: 'partial',
        payLink: latestUnfundedPayLink,
        statusDate: iwocaPayApplication?.status_date,
      };
    }

    return {
      pay_link_id: latestUnfundedPayLink.id,
      status: 'approved',
      payLink: latestUnfundedPayLink,
      statusDate: iwocaPayApplication?.status_date,
    };
  }

  if (iwocaPayApplication) {
    if (iwocaPayApplication.status === 'open') {
      return {
        pay_link_id: latestUnfundedPayLink.id,
        status: 'pending',
        payLink: latestUnfundedPayLink,
        statusDate: iwocaPayApplication?.status_date,
      };
    }

    if (iwocaPayApplication.status === 'declined') {
      return {
        pay_link_id: latestUnfundedPayLink.id,
        status: 'declined',
        payLink: latestUnfundedPayLink,
        statusDate: iwocaPayApplication?.status_date,
      };
    }
  }

  if (formalOfferWithHighestLimit?.is_timed_out) {
    return {
      pay_link_id: latestUnfundedPayLink.id,
      status: 'expired',
      payLink: latestUnfundedPayLink,
      statusDate: iwocaPayApplication?.status_date,
    };
  }

  return {
    pay_link_id: latestUnfundedPayLink.id,
    status: 'pending',
    payLink: latestUnfundedPayLink,
    statusDate: iwocaPayApplication?.status_date,
  };
};

const getAccountStatus = (account: Account) => {
  if (account.loan_status === 'ended') return 'ended';
  if (account.loan_status === 'overdue') return 'overdue';

  return 'ongoing';
};

const findPaylink = (
  payLinks: PayLinksResponse,
  payLinkId: string,
): PayLinksResponse[number] => {
  return payLinks.find((payLink) => payLink.id === payLinkId)!;
};

const getAccountMaxMonthlyInterestRate = (account: Account) => {
  const interestRates = account.interest_rates!;

  return interestRates[0].rate!;
};

const getTotalPrincipleFromSchedule = (account: Account) => {
  const totalPrinciple = account.scheduled_payments!.schedule!.reduce(
    (total, schedule) => total + schedule.principal,
    0,
  );

  return totalPrinciple;
};

export const getPaymentPlans = (
  accounts: Accounts,
  payLinks: PayLinksResponse,
): PaymentPlan[] => {
  const paymentPlans: PaymentPlan[] = [];
  if (!accounts) return [];

  for (const account of accounts) {
    if (!account.account_id) continue;
    if (account.product_type !== 'iwocapay') continue;
    if (
      account.loan_status !== 'duetoday' &&
      account.loan_status !== 'overdue' &&
      account.loan_status !== 'ongoing' &&
      account.loan_status !== 'ended'
    )
      continue;

    const payLink = findPaylink(payLinks, account.pay_link_id!);
    if (account.loan_status === 'ended') {
      paymentPlans.push({
        id: account.account_id!,
        pay_link_id: payLink.id,
        status: account.loan_status,
        account,
        payLink,
        interestRate: getAccountMaxMonthlyInterestRate(account),
      });
      continue;
    }

    paymentPlans.push({
      id: account.account_id!,
      pay_link_id: payLink.id,
      status: getAccountStatus(account),
      account,
      payLink: payLink,
      principle: getTotalPrincipleFromSchedule(account),
      interestRate: getAccountMaxMonthlyInterestRate(account),
    });
  }

  return paymentPlans;
};

export const getCapturedPlans = (
  capturedPayments: GetIwocapayPaymentCapturedResponse['data'],
  payLinks: PayLinksResponse,
): CapturedPayment[] => {
  const plans: CapturedPayment[] = [];

  for (const payment of capturedPayments) {
    if (!payment.is_fundable) continue;

    const payLink = payLinks.find(
      (payLink) => payLink.id === payment.pay_link.id,
    );

    if (!payLink) continue;

    plans.push({
      status: 'scheduled',
      pay_link_id: payLink.id,
      payLink,
      sellerProduct: payment.product,
    });
  }

  return plans;
};

export const getCancelledPlans = (
  capturedPayments: GetIwocapayPaymentCapturedResponse['data'],
  payLinks: PayLinksResponse,
): CancelledPlan[] => {
  const plans: CancelledPlan[] = [];

  for (const payment of capturedPayments) {
    if (!payment.cancelled_at) continue;

    const payLink = payLinks.find(
      (payLink) => payLink.id === payment.pay_link.id,
    );

    if (!payLink) continue;

    plans.push({
      status: 'cancelled',
      pay_link_id: payLink.id,
      payLink,
      cancelled_at: payment.cancelled_at,
    });
  }

  return plans;
};

export const getEndedPayments = (
  accounts: Accounts,
  payLinks: PayLinksResponse,
): EndedPaymentPlan[] => {
  const endedPaymentPlans: EndedPaymentPlan[] = [];

  for (const account of accounts) {
    if (!account.account_id) continue;
    if (account.product_type !== 'iwocapay') continue;
    if (getAccountStatus(account) !== 'ended') continue;

    const payLink = findPaylink(payLinks, account.pay_link_id!);

    endedPaymentPlans.push({
      id: account.account_id!,
      pay_link_id: payLink.id,
      status: 'ended',
      account,
      payLink,
      interestRate: getAccountMaxMonthlyInterestRate(account),
    });
  }

  return endedPaymentPlans;
};

export const getOngoingPaymentPlans = (paymentPlans: PaymentPlan[]) => {
  const ongoingPaymentPlans: OngoingPaymentPlan[] = [];

  paymentPlans.forEach((paymentPlan) => {
    if (paymentPlan.status === 'ongoing' || paymentPlan.status === 'overdue') {
      ongoingPaymentPlans.push(paymentPlan);
    }
  });

  return ongoingPaymentPlans;
};

export const getCreditLimit = (
  accounts: Accounts,
  totalScheduledAmount: number,
): number | null => {
  const formalOffer = getIwocaPayFormalOfferWithHighestLimit(accounts);

  if (!formalOffer) {
    return null;
  }

  return formalOffer.available_credit! - totalScheduledAmount;
};

export function getSuggestedProduct(product_offers: ProductOffers) {
  if (!product_offers) return;
  const suggestedProduct = product_offers.products.find(
    (product) => product.is_suggested === true,
  );

  return suggestedProduct;
}

export function checkStatusIsDeclined(product: SuggestedProduct) {
  if (!product.status) return false;
  return ['hard_declined', 'soft_declined', 'automatically_rejected'].includes(
    product.status,
  );
}

export function getProductDeclineUid(product: SuggestedProduct) {
  if (!product || !product.product_type_decline) return;
  return product.product_type_decline.uuid;
}

function formatObscuredDate(_: string, index: number) {
  return `${(index + 1) * 30} days`;
}

export function formatUnfundedCapturedPaymentDates(
  plan: Plan,
): ((date: string, index: number) => string) | undefined {
  if (!plan) return;
  if (plan.status !== 'approved' && plan.status !== 'scheduled') return;

  const payLink = plan.payLink;
  if (payLink.capture_delay_hours) return formatObscuredDate;
}

export const getTotalScheduledPayments = (
  payLinks: PayLinksResponse,
  capturedPayments: CapturedPaymentsResponse,
) => {
  const plans = getCapturedPlans(capturedPayments, payLinks);

  const totalScheduled = plans.reduce(
    (total, plan) => total + plan.payLink.amount,
    0,
  );

  return totalScheduled;
};

const getRequirementByResolutionMethod = (
  requirements: GetFundingRequirementsIwocapayResponse['data'] | null,
  resolution_method: GetFundingRequirementsIwocapayResponse['data'][number]['recommended_resolution_method'],
) => {
  return requirements?.find(
    (requirement) =>
      requirement.recommended_resolution_method === resolution_method,
  );
};

const getIsVisible = (
  requirement: GetFundingRequirementsIwocapayResponse['data'][number],
) => {
  return requirement.status !== 'completed';
};

export const checkAnyRequirementsVisible = (
  fundingRequirement: GetFundingRequirementsIwocapayResponse['data'] | null,
  resolution_methods: GetFundingRequirementsIwocapayResponse['data'][number]['recommended_resolution_method'][],
) => {
  for (const resolution_method of resolution_methods) {
    const requirement = getRequirementByResolutionMethod(
      fundingRequirement,
      resolution_method,
    );

    if (requirement && getIsVisible(requirement)) {
      return true;
    }
  }
  return false;
};

export const daysSinceStatusDate = (application: Application) => {
  const statusDate = application?.status_date;
  if (!statusDate) return null;

  const day = 1000 * 60 * 60 * 24;
  // TODO: let's not do this below
  return Math.ceil((Date.now() - Date.parse(statusDate)) / day);
};

export const getSpendingLimitStatus = ({
  spendingLimits,
  application,
}: {
  spendingLimits: { isValid: boolean };
  application: Application | null;
}) => {
  if (application?.status === 'declined') {
    return 'declined';
  }

  if (!spendingLimits.isValid) {
    return 'expired';
  }

  return 'valid';
};

enum DecisionErrorTypes {
  COMPANY_NOT_FOUND_ERROR = 'companyNotFound',
  DIRECTORS_COMPANY_MISMATCH_ERROR = 'directorsCompanyMismatch',
  NO_DIRECTORS_ERROR = 'noDirectors',
  INCOMPLETE_CREDIT_CHECK_ERROR = 'incompleteCreditCheck',
  DUPLICATE_GUARANTORS_ERROR = 'duplicateGuarantors',
  TECHNICAL_ERROR = 'technical',
  SOLE_TRADER_ADDITIONAL_GUARANTORS = 'soleTraderAdditionalGuarantors',
  NO_AFFORDABILITY_ASSESSMENT = 'noAffordabilityAssessment',
  COMPANY_DETAILS_INCORRECT = 'companyDetailsError',
}

const DECISION_ERROR_MAP = {
  '125': DecisionErrorTypes.COMPANY_NOT_FOUND_ERROR,
  '137': DecisionErrorTypes.DIRECTORS_COMPANY_MISMATCH_ERROR,
  '138': DecisionErrorTypes.NO_DIRECTORS_ERROR,
  '134': DecisionErrorTypes.INCOMPLETE_CREDIT_CHECK_ERROR,
  '136': DecisionErrorTypes.DUPLICATE_GUARANTORS_ERROR,
  '140': DecisionErrorTypes.SOLE_TRADER_ADDITIONAL_GUARANTORS,
  '106': DecisionErrorTypes.NO_AFFORDABILITY_ASSESSMENT,
  '107': DecisionErrorTypes.NO_AFFORDABILITY_ASSESSMENT,
  '141': DecisionErrorTypes.COMPANY_DETAILS_INCORRECT,
  '000': DecisionErrorTypes.TECHNICAL_ERROR,
} as const;

export const getApprovalRequirementErrors = (
  suggestedProduct: NonNullable<SuggestedProductType>,
) => {
  const errors = new Set<string>();

  for (const requirement of suggestedProduct.data_requirements) {
    if (!requirement.resolution_methods?.length) continue;

    for (const resolutionMethod of requirement.resolution_methods) {
      if (!resolutionMethod.errors?.length) continue;

      for (const error of resolutionMethod.errors) {
        if (!error.code) continue;

        // @ts-expect-error - dynamic key access
        const errorCode = DECISION_ERROR_MAP[error.code] ? error.code : '000';
        // @ts-expect-error - dynamic key access
        const errorType = DECISION_ERROR_MAP[errorCode];

        errors.add(errorType);
      }
    }
  }

  return errors;
};

const KNOWN_ERROR_CODE_PRIORITY = [
  '125',
  '137',
  '138',
  '134',
  '136',
  '141',
  '106',
  '107',
  '000',
] as const;

export function selectApprovalRequirementErrorWithHighestPriority(
  approvalRequirementError: ReturnType<typeof getApprovalRequirementErrors>,
) {
  for (const knownErrorCode of KNOWN_ERROR_CODE_PRIORITY) {
    const hasErrorCode = approvalRequirementError.has(
      DECISION_ERROR_MAP[knownErrorCode],
    );

    if (hasErrorCode) return DECISION_ERROR_MAP[knownErrorCode];
  }

  return DECISION_ERROR_MAP['000'];
}

export function useGetFlexiLoanAmount() {
  const { accounts, loadingAccounts } = useGetAccounts();

  const flexiLoan = accounts?.accounts?.find(
    (account: Account) =>
      (account.product_type === 'credit_facility' ||
        account.product_type === 'flexi24') &&
      account.loan_status !== 'ended',
  );

  return {
    flexiLoanAmount: flexiLoan?.loan_amount,
    loadingFlexiLoanAmount: loadingAccounts,
  };
}
